import type { Task } from '@/stores/tasks'

const CACHE_VERSION = 4 // bumped: isStanding
const CACHE_KEY_PREFIX = 'kb-tasks-cache'

export interface TaskCache {
  tasks: Task[]
  serverTimestamp: string
  departmentId: string | null
  version: number
}

/**
 * Strip heavy nested fields before caching.
 * comments, attachments are loaded per-task via fetchTask().
 * taskOwners/taskProjects keep only IDs + names for list rendering.
 */
function slimTask(task: Task): Task {
  return {
    id: task.id,
    humanId: task.humanId,
    title: task.title,
    description: task.description,
    status: task.status,
    priority: task.priority,
    prioritySort: task.prioritySort,
    progress: task.progress,
    startDate: task.startDate,
    plannedDate: task.plannedDate,
    plannedDateIsDefault: task.plannedDateIsDefault,
    isStanding: task.isStanding,
    hardDeadline: task.hardDeadline,
    responsibleDepartmentId: task.responsibleDepartmentId,
    tags: task.tags,
    excludedAiTags: task.excludedAiTags,
    autoTags: task.autoTags,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    // Keep slim owner info for avatar/name rendering
    taskOwners: task.taskOwners?.map((o) => ({
      person: { id: o.person.id, fullName: o.person.fullName },
      role: o.role,
    })),
    // Keep slim project info for project badges
    taskProjects: task.taskProjects?.map((tp) => ({
      id: tp.id,
      taskId: tp.taskId,
      projectId: tp.projectId,
      isPrimary: tp.isPrimary,
      addedAt: tp.addedAt,
      project: tp.project ? { id: tp.project.id, title: tp.project.title, slug: tp.project.slug } : undefined,
    })),
    responsibleDepartment: task.responsibleDepartment
      ? { id: task.responsibleDepartment.id, name: task.responsibleDepartment.name }
      : null,
    // Omit: comments, attachments (loaded per-task)
  }
}

function getCacheKey(departmentId: string | null): string {
  return `${CACHE_KEY_PREFIX}-${departmentId || 'all'}`
}

export function loadFromCache(departmentId: string | null): TaskCache | null {
  try {
    const key = getCacheKey(departmentId)
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const parsed: TaskCache = JSON.parse(raw)
    if (parsed.version !== CACHE_VERSION) {
      localStorage.removeItem(key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function saveToCache(
  tasks: Task[],
  serverTimestamp: string,
  departmentId: string | null,
): void {
  try {
    const key = getCacheKey(departmentId)
    const slimmed = tasks.map(slimTask)
    const cache: TaskCache = {
      tasks: slimmed,
      serverTimestamp,
      departmentId,
      version: CACHE_VERSION,
    }
    localStorage.setItem(key, JSON.stringify(cache))
  } catch {
    // localStorage full — clear old caches and retry once
    clearAllCaches()
    try {
      const key = getCacheKey(departmentId)
      const slimmed = tasks.map(slimTask)
      const cache: TaskCache = {
        tasks: slimmed,
        serverTimestamp,
        departmentId,
        version: CACHE_VERSION,
      }
      localStorage.setItem(key, JSON.stringify(cache))
    } catch {
      console.warn('[TaskCache] Failed to save — data exceeds localStorage quota')
    }
  }
}

export function clearCache(departmentId: string | null): void {
  localStorage.removeItem(getCacheKey(departmentId))
}

export function clearAllCaches(): void {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(CACHE_KEY_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k))
}

/**
 * Merge cached tasks with delta updates from server.
 * Updates existing tasks, adds new ones, removes deleted ones.
 */
export function mergeCache(
  existing: Task[],
  updates: Task[],
  deletedIds: string[],
): Task[] {
  const taskMap = new Map<string, Task>()

  // Start with existing tasks
  for (const task of existing) {
    taskMap.set(task.id, task)
  }

  // Remove deleted
  for (const id of deletedIds) {
    taskMap.delete(id)
  }

  // Apply updates (overwrite existing or add new).
  // Archived tasks are removed from cache — they belong to the snapshot.
  for (const task of updates) {
    if ((task as any).isArchived) {
      taskMap.delete(task.id)
    } else {
      taskMap.set(task.id, task)
    }
  }

  return Array.from(taskMap.values())
}

/**
 * Update a single task entry in the cache (for WebSocket write-through).
 */
export function updateCacheEntry(
  task: Task,
  departmentId: string | null,
): void {
  const cached = loadFromCache(departmentId)
  if (!cached) return

  const slim = slimTask(task)
  const index = cached.tasks.findIndex((t) => t.id === task.id)
  if (index !== -1) {
    cached.tasks[index] = slim
  } else {
    cached.tasks.unshift(slim)
  }

  saveToCache(cached.tasks, cached.serverTimestamp, departmentId)
}

/**
 * Remove a task from the cache (for WebSocket delete events).
 */
export function removeCacheEntry(
  taskId: string,
  departmentId: string | null,
): void {
  const cached = loadFromCache(departmentId)
  if (!cached) return

  cached.tasks = cached.tasks.filter((t) => t.id !== taskId)
  saveToCache(cached.tasks, cached.serverTimestamp, departmentId)
}
